/**
 * Content Carousel
 *
 * Features:
 * - Horizontal slide transitions (transform: translateX)
 * - Manual navigation only (arrows + dots, no autoplay, no touch/swipe)
 * - Keyboard navigation (Arrow Left/Right, Home/End)
 * - ARIA live regions for screen reader announcements
 * - Reduced motion support
 * - Multiple carousel instances per page
 * - Progressive enhancement (first slide visible without JS)
 */

// Global registry for multiple carousel instances
const carouselInstances = new WeakMap();

class Carousel {
  constructor(element) {
    if (!element) return;

    this.element = element;
    this.track = element.querySelector('[data-carousel-track]');
    this.slides = element.querySelectorAll('[data-carousel-slide-wrapper]');
    this.prevButton = element.querySelector('[data-carousel-prev]');
    this.nextButton = element.querySelector('[data-carousel-next]');
    this.dots = element.querySelectorAll('[data-carousel-dot]');
    this.statusRegion = element.querySelector('[data-carousel-status]');

    this.currentIndex = 0;
    this.isTransitioning = false;
    this.slideCount = this.slides.length;

    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Validate carousel has slides
    if (this.slideCount === 0) {
      console.warn('Carousel: No slides found');
      return;
    }

    // Hide controls if only one slide
    if (this.slideCount === 1) {
      this.hideControls();
      return;
    }

    this.init();
  }

  /**
   * Initialize carousel
   */
  init() {
    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.navigate(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.navigate(1));
    }

    // Dot navigation (event delegation)
    this.dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.slideIndex, 10);
        if (!isNaN(index)) {
          this.navigateToIndex(index);
        }
      });
    });

    // Keyboard navigation
    this.setupKeyboardNavigation();

    // Set initial slide position
    this.updateSlidePositions();
  }

  /**
   * Navigate by direction
   * @param {number} direction - -1 for previous, 1 for next
   */
  navigate(direction) {
    if (this.isTransitioning) return;

    let newIndex = this.currentIndex + direction;

    // Loop navigation
    if (newIndex < 0) {
      newIndex = this.slideCount - 1;
    } else if (newIndex >= this.slideCount) {
      newIndex = 0;
    }

    this.navigateToIndex(newIndex);
  }

  /**
   * Navigate to specific slide index
   * @param {number} index - Target slide index (0-based)
   */
  navigateToIndex(index) {
    // Validation
    if (index === this.currentIndex || this.isTransitioning) return;
    if (index < 0 || index >= this.slideCount) return;

    this.isTransitioning = true;

    // Update current index
    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    // Update slide positions (transform)
    this.updateSlidePositions();

    // Update ARIA states
    this.updateAriaStates(index, previousIndex);

    // Update dot navigation
    this.updateDots(index);

    // Announce to screen readers
    this.announceSlideChange(index);

    // Reset transition lock
    const transitionDuration = this.prefersReducedMotion ? 0 : 500;
    setTimeout(() => {
      this.isTransitioning = false;
    }, transitionDuration);
  }

  /**
   * Update slide positions using transform
   */
  updateSlidePositions() {
    const offset = -this.currentIndex * 100;

    if (this.prefersReducedMotion) {
      this.track.style.transition = 'none';
    } else {
      this.track.style.transition = 'transform 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)';
    }

    this.track.style.transform = `translateX(${offset}%)`;
  }

  /**
   * Update ARIA states
   */
  updateAriaStates(activeIndex, previousIndex) {
    // Update slides
    this.slides.forEach((slide, index) => {
      if (index === activeIndex) {
        slide.setAttribute('aria-current', 'true');
      } else {
        slide.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Update dot navigation states
   */
  updateDots(activeIndex) {
    this.dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add('carousel__dot--active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('carousel__dot--active');
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Announce slide change to screen readers
   */
  announceSlideChange(index) {
    if (!this.statusRegion) return;

    const slideNumber = index + 1;
    const announcement = `Slide ${slideNumber} of ${this.slideCount}`;

    this.statusRegion.textContent = announcement;
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    this.element.addEventListener('keydown', (event) => {
      // Only respond if carousel or its children are focused
      if (!this.element.contains(document.activeElement)) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.navigate(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.navigate(1);
          break;
        case 'Home':
          event.preventDefault();
          this.navigateToIndex(0);
          break;
        case 'End':
          event.preventDefault();
          this.navigateToIndex(this.slideCount - 1);
          break;
      }
    });
  }

  /**
   * Hide controls for single slide
   */
  hideControls() {
    if (this.prevButton) this.prevButton.style.display = 'none';
    if (this.nextButton) this.nextButton.style.display = 'none';

    const dotsContainer = this.element.querySelector('.carousel__dots');
    if (dotsContainer) dotsContainer.style.display = 'none';
  }

  /**
   * Destroy carousel instance (cleanup)
   */
  destroy() {
    // Remove event listeners would go here if needed
    this.isTransitioning = false;
  }
}

/**
 * Initialize all carousels on the page
 */
function initCarousels(container = document) {
  const carouselElements = container.querySelectorAll('[data-carousel]');

  carouselElements.forEach(element => {
    // Destroy existing instance if it exists
    const existingCarousel = carouselInstances.get(element);
    if (existingCarousel) {
      existingCarousel.destroy();
    }

    // Create new instance
    const carousel = new Carousel(element);
    carouselInstances.set(element, carousel);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarousels);
} else {
  initCarousels();
}

// Shopify Theme Customizer: Re-initialize when section is loaded/reloaded
document.addEventListener('shopify:section:load', function (event) {
  const container = event.target;
  initCarousels(container);
});

// Shopify Theme Customizer: Re-initialize when block is selected
document.addEventListener('shopify:block:select', function (event) {
  const container = event.target.closest('[data-section-id]');
  if (container) {
    initCarousels(container);
  }
});

// Shopify Theme Customizer: Clean up when section is unloaded
document.addEventListener('shopify:section:unload', function (event) {
  const container = event.target;
  const carouselElements = container.querySelectorAll('[data-carousel]');

  carouselElements.forEach(element => {
    const carousel = carouselInstances.get(element);
    if (carousel) {
      carousel.destroy();
      carouselInstances.delete(element);
    }
  });
});

// Export for module systems (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Carousel;
}
